/**
 * REST API client の共通 request / retry / response 検証を提供する。
 */

import type { QueryParams } from './buildQueryString'
import { fetchWithTimeout } from './fetchWithTimeout'
import { sleep } from './sleep'

type Schema<T> = {
  parse: (data: unknown) => T
}

export type RestApiClient = {
  /** request 実行 */
  run: <T>(params: {
    method: 'GET' | 'POST'
    path: string
    query?: QueryParams
    body?: Record<string, unknown>
    responseSchema: Schema<T>
  }) => Promise<T>
}

type BuildRequestData = (params: {
  method: 'GET' | 'POST'
  path: string
  query?: QueryParams
  body?: Record<string, unknown>
}) => Promise<{
  url: string
  headers?: Record<string, string>
  body?: string
}>

type GetDelayForRetry = (params: {
  statusCode: number
  errorMessage: string
}) => number | undefined

/**
 * HTTP error response を取引所固有の型付き例外へ分類する。
 * 設定不備など retry で解消しないエラーを、汎用エラーと区別するために使う。
 */
type ClassifyErrorResponse = (params: {
  statusCode: number
  errorMessage: string
}) => Error | undefined

type ValidateResponse = (params: {
  data: unknown
}) =>
  | {
      status: 'error'
      error: unknown
    }
  | {
      status: 'retry'
      delay: number
      error: unknown
    }
  | {
      status: 'success'
    }

type CreateRestApiClient = (params: {
  /** REST API request に必要な url , headers , body を生成 */
  buildRequestData: BuildRequestData
  /** request 失敗時の response結果から retry の必要性及び retry 実行までの遅延時間を取得 / retry 不要の場合は undefined */
  getDelayForRetry: GetDelayForRetry
  /** HTTP error response を型付き例外へ分類する / 分類できない場合は undefined */
  classifyErrorResponse?: ClassifyErrorResponse
  /** response data の検証を行う */
  validateResponse?: ValidateResponse
  /** タイムアウト後の retry 実行までの遅延（待機）時間 */
  delayTimeout?: number
  /** リトライの最大回数 */
  maxRetry?: number
  /** request を proxy する場合の proxy base URL */
  proxy?: string
  /** リクエストのタイムアウト時間 */
  timeout?: number
}) => RestApiClient

/**
 * network / timeout 系の retry 対象 error かを判定する。
 * @param error - 判定対象の error
 * @returns retry 対象の場合は true
 */
const isRetryableRequestError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.message.includes('fetch') || error.message.includes('timed out'))

/**
 * Response の JSON body を読み取り、空 body の場合は分かりやすい error を返す。
 * @param response - 対象の response
 * @returns JSON body
 */
const parseResponseJson = async (params: {
  response: Response
}): Promise<unknown> => {
  const { response } = params
  const responseText = await response.clone().text()

  if (responseText.trim() === '') {
    throw new Error('Response body is empty')
  }

  return await response.json()
}

export const createRestApiClient: CreateRestApiClient = ({
  buildRequestData,
  getDelayForRetry,
  classifyErrorResponse,
  validateResponse,
  delayTimeout = 1000, // デフォルトのタイムアウト後の遅延（待機）時間 = 1秒
  proxy,
  maxRetry = 3, // デフォルトのリトライ回数 = 3
  timeout = 5000, // デフォルトのタイムアウト = 5秒
}) => {
  const request = async <T>(params: {
    method: 'GET' | 'POST'
    path: string
    query?: QueryParams
    body?: Record<string, unknown>
    responseSchema: Schema<T>
  }): Promise<T> => {
    const { body, method, path, query, responseSchema } = params
    let retryCount = 0

    while (true) {
      const {
        body: requestBody,
        headers,
        url,
      } = await buildRequestData({
        body,
        method,
        path,
        query,
      })

      const proxyUrl = proxy ? `${proxy}${encodeURIComponent(url)}` : url

      let response: Response

      try {
        response = await fetchWithTimeout(
          proxyUrl,
          {
            method,
            headers,
            body: requestBody,
          },
          timeout,
        )
      } catch (error) {
        if (retryCount < maxRetry && isRetryableRequestError(error)) {
          retryCount += 1
          await sleep(delayTimeout)
          continue
        }
        throw error
      }

      if (!response.ok) {
        const statusCode = response.status
        const text = await response.text()
        const errorMessage = text === '' ? response.statusText : text

        const delayForRetry = getDelayForRetry({
          statusCode,
          errorMessage,
        })

        if (retryCount < maxRetry && delayForRetry !== undefined) {
          retryCount += 1
          await sleep(delayForRetry)
          continue
        }

        // 設定不備など retry で解消しないエラーは型付き例外として投げ分ける
        const classifiedError = classifyErrorResponse?.({
          statusCode,
          errorMessage,
        })
        if (classifiedError !== undefined) {
          throw classifiedError
        }

        throw new Error(`${statusCode} ${errorMessage}`)
      }

      const data = await parseResponseJson({ response })

      if (validateResponse) {
        const validationResult = validateResponse({ data })
        switch (validationResult.status) {
          case 'error': {
            throw validationResult.error
          }
          case 'retry': {
            if (retryCount < maxRetry) {
              retryCount += 1
              await sleep(validationResult.delay)
              continue
            }
            throw validationResult.error
          }
          case 'success': {
            break
          }
          default: {
            throw new Error(
              `Unknown status: ${validationResult satisfies never}`,
            )
          }
        }
      }

      return responseSchema.parse(data)
    }
  }

  return {
    run: (params) =>
      request({
        ...params,
      }),
  }
}
